import { getSections } from "@/shared/repositories/SectionRepository";
import {
	CampaignEventData,
	RollChangeStats,
} from "../models/CampaignEventData";
import { useCalendarAreaId } from "../context/CalendarAreaContext";
import {
	Event,
	EventsToCompareResult,
	ProductionEventActual,
} from "../models/Event";
import {
	getCampaignsByEventId,
	getEvents,
	getEventsToCompare,
	getPendingConfirmEvents,
} from "../repositories/CalendarRepository";
import { useQuery } from "@tanstack/react-query";

export const useSearchEvents = ({
	start,
	end,
	eventId,
	monthStart,
	dayBreakdown = false,
	stopRefetch = false,
	isDraftMode = false,
}: {
	start?: Date;
	end?: Date;
	eventId?: number;
	monthStart?: Date | null;
	dayBreakdown?: boolean;
	stopRefetch?: boolean;
	isDraftMode?: boolean;
}) => {
	const defaultAreaId = useCalendarAreaId();
	return useQuery<Event[], Error>({
		queryKey: [
			"event",
			start,
			end,
			eventId,
			monthStart,
			dayBreakdown,
			isDraftMode,
			defaultAreaId,
		],
		queryFn: ({ signal }) =>
			getEvents(
				{
					start,
					end,
					eventId,
					monthStart,
					dayBreakdown,
					isDraftMode,
					defaultAreaId,
				},
				signal,
			),
		refetchInterval: stopRefetch ? undefined : 60_000,
	});
};

export const useSearchCampaignsByEventId = ({
	eventId,
	isDraftMode,
}: {
	eventId: number;
	isDraftMode: boolean;
}) => {
	const defaultAreaId = useCalendarAreaId();
	return useQuery<
		{
			campaigns: CampaignEventData[];
			rollChangeStats: RollChangeStats | null;
			actuals: ProductionEventActual[];
		},
		Error
	>({
		queryKey: ["campaigns", eventId, isDraftMode, defaultAreaId],
		queryFn: ({ signal }) =>
			getCampaignsByEventId({ eventId, isDraftMode, defaultAreaId }, signal),
	});
};

export const useRollChangeStats = ({
	sectionId,
	start,
	enabled = true,
}: {
	sectionId: number | null;
	start: Date | null;
	enabled?: boolean;
}) => {
	const defaultAreaId = useCalendarAreaId();
	return useQuery<RollChangeStats | null, Error>({
		queryKey: [
			"rollChangeStats",
			sectionId,
			start?.toISOString(),
			defaultAreaId,
		],
		queryFn: async ({ signal }) => {
			const result = await getSections(
				{ sectionId: sectionId!, start: start!, defaultAreaId },
				signal,
			);
			return result.rollChangeStats;
		},
		enabled: enabled && sectionId != null && start != null,
	});
};

export const useSearchPendingConfirmEvents = ({
	callSearch,
	monthEnd,
}: {
	callSearch: boolean;
	monthEnd: Date | null;
}) => {
	const defaultAreaId = useCalendarAreaId();
	return useQuery<Event[], Error>({
		queryKey: ["pendingConfirmEvents", monthEnd, callSearch, defaultAreaId],
		queryFn: ({ signal }) => {
			if (callSearch) {
				return getPendingConfirmEvents({ monthEnd, defaultAreaId }, signal);
			} else {
				return [];
			}
		},
	});
};

export const useSearchEventsToCompare = ({
	callSearch,
}: {
	callSearch: boolean;
}) => {
	const defaultAreaId = useCalendarAreaId();
	return useQuery<EventsToCompareResult, Error>({
		queryKey: ["eventsToCompare", callSearch, defaultAreaId],
		queryFn: ({ signal }) => {
			if (callSearch) {
				return getEventsToCompare({ defaultAreaId }, signal);
			} else {
				return { mainEvents: [], draftEvents: [] };
			}
		},
	});
};
